import { useEffect, useState } from 'react';
import type { Socket } from 'socket.io-client';
import { io } from 'socket.io-client';

import { getWsApi } from '../api';

import logger from './logger';

interface UseSocketReturn {
  socket: Socket | null;
  handleLogout: () => void;
}

/**
 * Custom hook for Socket.io connection management.
 * Establishes WebSocket connection, handles authentication token exchange,
 * and manages connection lifecycle events.
 */
export function useSocket(
  setView: React.Dispatch<React.SetStateAction<string>>,
  logout: () => void
): UseSocketReturn {
  const [socket, setSocket] = useState<Socket | null>(null);

  useEffect(() => {
    let newSocket: Socket | null = null;

    const createSocket = (): void => {
      logger.debug('Creating socket connection to:', getWsApi());
      newSocket = io(getWsApi(), {
        autoConnect: true,
        reconnection: true,
        reconnectionDelay: 2000,
        reconnectionAttempts: 10,
        timeout: 30000,
        transports: ['polling', 'websocket'],
        forceNew: true,
      });

      setSocket(newSocket);

      const onConnect = (): void => {
        logger.debug('Socket connected:', newSocket?.id);

        setView((prevView) => {
          if (prevView === 'offline') {
            logger.debug('Got connection!');
            return 'init';
          }
          return prevView;
        });

        const token = window.localStorage.getItem('token');
        if (token) {
          newSocket?.emit('token', { token });
        }
      };

      const onDisconnect = (reason: string): void => {
        logger.debug('Socket disconnected:', reason);
        if (reason === 'io server disconnect') {
          setView('offline');
        }
      };

      const onConnectError = (err: Error): void => {
        logger.error('Socket connection error:', err);
        logger.error('Error details:', err.message);
        setView('offline');
      };

      const onToken = (data: { token: string | null }): void => {
        if (data.token === null || data.token === 'null') {
          window.localStorage.removeItem('token');
        } else {
          window.localStorage.setItem('token', data.token);
        }
        logger.debug('Got replacement token');
        setView('init');
      };

      newSocket.on('connect', onConnect);
      newSocket.on('disconnect', onDisconnect);
      newSocket.on('connect_error', onConnectError);
      newSocket.on('token', onToken);
    };

    createSocket();

    return () => {
      if (newSocket) {
        newSocket.off('connect');
        newSocket.off('disconnect');
        newSocket.off('connect_error');
        newSocket.off('token');
        newSocket.close();
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout = (): void => {
    if (socket) {
      socket.emit('token', { token: null });
    }
    logout();
    setView('init');
  };

  return { socket, handleLogout };
}
